import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Download, Search, ArrowUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { UKDatePicker } from '../ui/date-picker-uk';
import OrderChip from '../Brand/OrderChip';
import { SkeletonRows } from '../Brand/BrandLoading';
import { ukDate, money } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'custom', label: 'Custom' },
];

const COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'order_number', label: 'Order no.' },
  { key: 'job_type', label: 'Job type' },
  { key: 'client_name', label: 'Customer' },
  { key: 'delivery_status', label: 'Status' },
  { key: 'job_value', label: 'Price charged', numeric: true },
  { key: 'wage', label: 'Staff wages', numeric: true },
  { key: 'travel', label: 'Travel money', numeric: true },
  { key: 'bonus', label: 'Bonuses', numeric: true },
  { key: 'ingredients_cost', label: 'Ingredients', numeric: true },
  { key: 'total_costs', label: 'Total costs', numeric: true },
  { key: 'profit_gbp', label: 'Profit £', numeric: true },
  { key: 'profit_percentage', label: 'Profit %', numeric: true },
];

export const ProfitPerJob = ({ token, staff = [] }) => {
  const [period, setPeriod] = useState('this_month');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState({ job_type: '', staff_email: '', status: '', search: '' });
  const [sort, setSort] = useState({ key: 'date', dir: 'asc' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const query = useCallback(() => {
    const params = new URLSearchParams({ period });
    if (period === 'custom') {
      params.set('start_date', custom.start);
      params.set('end_date', custom.end);
    }
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
    return params.toString();
  }, [period, custom, filters]);

  const load = useCallback(async () => {
    if (period === 'custom' && (!custom.start || !custom.end)) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/profit-per-job?${query()}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load profit per job');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const colourFor = (percentage) => {
    if (percentage === null || percentage === undefined) return 'text-slate-400';
    const s = data?.settings || { green_min: 40, amber_min: 20 };
    if (percentage >= s.green_min) return 'text-green-700 font-semibold';
    if (percentage >= s.amber_min) return 'text-amber-600 font-semibold';
    return 'text-red-600 font-semibold';
  };

  const rows = [...(data?.jobs || [])].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const av = a[sort.key], bv = b[sort.key];
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/profit-per-job/export?${query()}`, { headers, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = 'butler-direct-profit-per-job.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('Could not build that download');
    }
  };

  return (
    <Card className="bg-white" data-testid="profit-per-job">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="bd-heading text-base text-ink">Profit per job</h3>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`profit-period-${p.key}`}
              className={`min-h-8 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                period === p.key ? 'bg-gold text-ink border-gold'
                  : 'bg-white text-slate-600 border-hair hover:border-gold hover:bg-cream'
              }`}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-end gap-2">
              <UKDatePicker label="From" value={custom.start} onChange={(value) => setCustom({ ...custom, start: value })} testId="profit-custom-start" />
              <UKDatePicker label="To" value={custom.end} onChange={(value) => setCustom({ ...custom, end: value })} testId="profit-custom-end" />
            </div>
          )}
          <Button size="sm" variant="outline" className="ml-auto" onClick={download} data-testid="profit-export-csv">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <select
            className=""
            value={filters.job_type}
            onChange={(e) => setFilters({ ...filters, job_type: e.target.value })}
            data-testid="profit-filter-jobtype"
          >
            <option value="">All job types</option>
            {(data?.job_types || []).map((type) => <option key={type} value={type}>{type}</option>)}
          </select>

          <select
            className=""
            value={filters.staff_email}
            onChange={(e) => setFilters({ ...filters, staff_email: e.target.value })}
            data-testid="profit-filter-staff"
          >
            <option value="">All staff</option>
            {staff.map((member) => <option key={member.email} value={member.email}>{member.name}</option>)}
          </select>

          <select
            className=""
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="profit-filter-status"
          >
            <option value="">All statuses</option>
            <option value="booked">Booked</option>
            <option value="delivered">Delivered</option>
            <option value="holding">Holding</option>
          </select>

          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2.5" />
            <Input
              placeholder="Search order no. or customer"
              className="h-9 pl-8 w-64"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="profit-search"
            />
          </div>
        </div>

        {loading && <SkeletonRows rows={6} testId="profit-loading" />}

        {!loading && data && (
          <div className="max-h-[70vh] overflow-auto rounded-[10px] border border-hair">
            <table className="bd-table text-xs">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`cursor-pointer select-none ${col.numeric ? 'bd-num' : 'text-left'}`}
                      onClick={() => setSort({ key: col.key, dir: sort.key === col.key && sort.dir === 'asc' ? 'desc' : 'asc' })}
                      data-testid={`profit-sort-${col.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sort.key === col.key && <ArrowUpDown className="w-3 h-3" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row.job_id}>
                    <tr
                      className="cursor-pointer"
                      onClick={() => setExpanded(expanded === row.job_id ? null : row.job_id)}
                      data-testid={`profit-row-${row.job_id}`}
                    >
                      <td className="whitespace-nowrap">{ukDate(row.date)}</td>
                      <td><OrderChip value={row.order_number} /></td>
                      <td>{row.job_type}</td>
                      <td>{row.client_name}</td>
                      <td>
                        <Badge variant="outline" className="text-[10px] capitalize">{row.delivery_status}</Badge>
                      </td>
                      <td className="bd-num">{row.has_price ? money(row.job_value) : <span className="text-slate-400">No price</span>}</td>
                      <td className="bd-num">{money(row.wage)}</td>
                      <td className="bd-num">{money(row.travel)}</td>
                      <td className="bd-num">{money(row.bonus)}</td>
                      <td className="bd-num">{money(row.ingredients_cost)}</td>
                      <td className="bd-num">{money(row.total_costs)}</td>
                      <td className="bd-num">{row.has_price ? money(row.profit_gbp) : <span className="text-slate-400">No price</span>}</td>
                      <td className={`bd-num ${colourFor(row.profit_percentage)}`} data-testid={`profit-pct-${row.job_id}`}>
                        {row.profit_percentage === null ? 'No price' : `${row.profit_percentage.toFixed(1)}%`}
                      </td>
                    </tr>
                    {expanded === row.job_id && (
                      <tr className="bg-cream" data-testid={`profit-breakdown-${row.job_id}`}>
                        <td colSpan={COLUMNS.length} className="p-3">
                          <p className="bd-heading mb-1 text-[11px] text-gold-deep">Cost breakdown</p>
                          <ul className="text-slate-600 space-y-0.5">
                            <li>Staff wages ({row.assigned_staff_count} × £{(row.wage / (row.assigned_staff_count || 1)).toFixed(2)}): {money(row.wage)}</li>
                            <li>Travel money: {money(row.travel)}</li>
                            <li>Bonuses: {money(row.bonus)}</li>
                            <li>Ingredients &amp; supplies: {money(row.ingredients_cost)}</li>
                            <li className="font-semibold text-slate-800">Total costs: {money(row.total_costs)}</li>
                            <li>{row.has_price ? `Price charged: ${money(row.job_value)}` : 'No price entered for this job'}</li>
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                <tr className="bd-total" data-testid="profit-totals-row">
                  <td colSpan={5}>TOTAL · {data.totals.job_count} jobs{data.totals.no_price_count ? ` (${data.totals.no_price_count} with no price)` : ''}</td>
                  <td className="bd-num">{money(data.totals.total_value)}</td>
                  <td className="bd-num">{money(data.totals.total_wages)}</td>
                  <td className="bd-num">{money(data.totals.total_travel)}</td>
                  <td className="bd-num">{money(data.totals.total_bonus)}</td>
                  <td className="bd-num">{money(data.totals.total_ingredients)}</td>
                  <td className="bd-num">{money(data.totals.total_costs)}</td>
                  <td className="bd-num">{money(data.totals.total_profit)}</td>
                  <td className={`bd-num ${colourFor(data.totals.profit_percentage)}`}>
                    {data.totals.profit_percentage === null ? '—' : `${data.totals.profit_percentage.toFixed(1)}%`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-slate-400">
          Green {data?.settings?.green_min ?? 40}%+ · amber {data?.settings?.amber_min ?? 20}-{(data?.settings?.green_min ?? 40) - 1}% · red below {data?.settings?.amber_min ?? 20}%. Thresholds are editable in Settings.
        </p>
      </CardContent>
    </Card>
  );
};

export default ProfitPerJob;
